import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { COMPETENCY_CATEGORY_LABELS } from 'app/_models/competency.model';
import { AssetTypesService } from 'app/_services/asset-types.service';
import { FileService } from 'app/_services/file.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteService } from 'app/_services/site.service';
import { SiteFormsService } from 'app/_services/site-forms.service';
import { SiteWorkOrderProceduresService } from 'app/_services/site-work-order-procedures.service';
import { EditCompetenciesComponent } from 'app/shared-pages/work-order/components/edit-competencies/edit-competencies.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'procedure-details-page',
  templateUrl: './procedure-details-page.component.html',
})
export class ProcedureDetailsPageComponent implements OnInit {
  public selectedSiteId: any;
  public selectedProcedureId: any;
  public selectedSite: any;
  public procedure: any;
  public leadWorkerCompetencies: any[] = [];
  public allWorkerCompetencies: any[] = [];
  public activeTab: string = 'asset';
  public loading = true;
  public deleting = false;
  public assetCategories: any[] = [];
  public selectedAssetCategory: any;
  public assetCategorySaving = false;
  public assetCategoriesLoading = false;
  public allowCompulsoryCheckOnSiteForm = false;
  public allowCompulsoryCheckOffSiteForm = false;
  public siteFormsOptions: any[] = [];
  public selectedCheckOnSiteForm: any;
  public selectedCheckOffSiteForm: any;
  public selectedDocuments: File[] = [];
  public checkOnSiteFormSaving = false;
  public checkOffSiteFormSaving = false;
  public documentSaving = false;
  public deletingDocumentId: number = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private siteService: SiteService,
    private procedureService: SiteWorkOrderProceduresService,
    private assetTypesService: AssetTypesService,
    private siteFormsService: SiteFormsService,
    private fileService: FileService,
    private notificationService: NotificationService,
    private globals: Globals,
  ) {}

  ngOnInit(): void {
    this.selectedSiteId = this.activatedRoute.snapshot.params.id;
    this.selectedProcedureId = this.activatedRoute.snapshot.params.procedureId;

    this.loadSite();
    this.loadProcedure();
    this.loadAssetCategories();
  }

  private loadSite() {
    this.siteService.getSiteById(this.selectedSiteId).subscribe((data: any) => {
      this.selectedSite = data;
      this.allowCompulsoryCheckOnSiteForm = !!data.siteOwner.siteOwnerUser.allowCompulsoryCheckOnSiteForm;
      this.allowCompulsoryCheckOffSiteForm = !!data.siteOwner.siteOwnerUser.allowCompulsoryCheckOffSiteForm;
      if (this.allowCompulsoryCheckOnSiteForm || this.allowCompulsoryCheckOffSiteForm) {
        this.loadSiteForms();
      }
    });
  }

  private loadProcedure() {
    this.loading = true;
    this.procedureService.getWorkOrderProcedureById(this.selectedProcedureId).subscribe((data: any) => {
      this.procedure = data;
      this.setCompetencySections();
      this.selectedAssetCategory = null;
      this.syncSelectedSiteForms();
      this.loading = false;
    }, error => {
      this.loading = false;
      throw error;
    });
  }

  private loadSiteForms() {
    const pagination = {
      size: 10000,
      page: 0,
      totalRecords: 0,
    };

    this.siteFormsService.getAllBySite(this.selectedSiteId, pagination, ['siteForm.name']).subscribe((siteForms: any) => {
      this.siteFormsOptions = [{ label: 'Select site form...', value: null }];

      siteForms.body.forEach((element) => {
        this.siteFormsOptions.push({
          label: element.name,
          value: element.id,
        });
      });

      this.syncSelectedSiteForms();
    });
  }

  private syncSelectedSiteForms() {
    if (!this.procedure) {
      return;
    }

    this.selectedCheckOnSiteForm = this.findSiteFormOption(this.procedure.checkOnSiteFormId);
    this.selectedCheckOffSiteForm = this.findSiteFormOption(this.procedure.checkOffSiteFormId);
  }

  private findSiteFormOption(siteFormId: number | null) {
    if (siteFormId === null || siteFormId === undefined) {
      return null;
    }

    return this.siteFormsOptions.find((option) => option.value === siteFormId) || null;
  }

  private loadAssetCategories() {
    this.assetCategoriesLoading = true;
    this.assetTypesService.getAllAssetTypesForSite(this.selectedSiteId).subscribe((data: any) => {
      this.assetCategories = data.map((element) => ({
        label: element.name,
        value: element,
      }));
      this.assetCategoriesLoading = false;
    }, error => {
      this.assetCategoriesLoading = false;
      throw error;
    });
  }

  public setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  public hasCompetenciesFor(workerLevel: 'LEAD' | 'ALL'): boolean {
    return workerLevel === 'ALL' ? this.allWorkerCompetencies.length > 0 : this.leadWorkerCompetencies.length > 0;
  }

  public onEditCompetencies(workerLevel: 'LEAD' | 'ALL') {
    const modalOptions: NgbModalOptions = { centered: true, size: 'lg', windowClass: 'procedure-competency-modal' };
    const modal = this.modalService.open(EditCompetenciesComponent, modalOptions);
    modal.componentInstance.procedure = this.procedure;
    modal.componentInstance.siteOwnerId = this.selectedSite.siteOwner.id;
    modal.componentInstance.workerLevel = workerLevel;

    modal.result.then((result) => {
      if (result && result.competencyTypeId) {
        this.addProcedureCompetency(result);
      }
    }).catch(() => { /* modal dismissed */ });
  }

  public onCompetencyDelete(competency: any) {
    const remainingCompetencies = this.getProcedureCompetencies()
      .filter((item) => Number(this.getCompetencyTypeId(item)) !== Number(competency.competencyTypeId || competency.id));

    this.procedureService.updateWorkOrderProcedure(this.buildProcedureUpdatePayload(remainingCompetencies, this.getTopLevelTurbineFields(remainingCompetencies))).subscribe(
      () => {
        this.notificationService.notify('success', 'Deleted!', 'Competency has been deleted.');
        this.loadProcedure();
      },
      () => {
        this.notificationService.notify('danger', 'Error', 'There was an error deleting the competency.');
      },
    );
  }

  private setCompetencySections(): void {
    const competencies = this.getProcedureCompetencies().map((item) => this.mapProcedureCompetency(item));

    this.leadWorkerCompetencies = competencies.filter((competency) => !competency.applyToParty);
    this.allWorkerCompetencies = competencies.filter((competency) => competency.applyToParty);
  }

  private mapProcedureCompetency(competency: any): any {
    const rawType = competency.competencyType || '';
    const shortName = competency.competencyTypeShortName;
    const fullName = competency.competencyTypeName || '-';
    const hasTurbineFields = !!(
      competency.manufacturer ||
      competency.oem ||
      competency.model ||
      competency.rating ||
      (Array.isArray(competency.models) && competency.models.length) ||
      (Array.isArray(competency.ratings) && competency.ratings.length) ||
      this.procedure.manufacturer ||
      this.normalizeProcedureArrayValues(this.procedure.models).length ||
      this.normalizeProcedureArrayValues(this.procedure.ratings).length
    );
    const normalizedType = rawType || (hasTurbineFields ? 'TURBINE' : '');
    const isTurbine = normalizedType === 'TURBINE';

    return {
      id: competency.id || competency.competencyTypeId,
      competencyTypeId: this.getCompetencyTypeId(competency),
      type: COMPETENCY_CATEGORY_LABELS[normalizedType] || normalizedType || 'Competency',
      name: shortName || fullName,
      oem: isTurbine ? (competency.manufacturer || this.procedure.manufacturer || undefined) : undefined,
      model: isTurbine
        ? (Array.isArray(competency.models)
          ? competency.models.join(', ')
          : this.joinProcedureValues(this.procedure.models))
        : undefined,
      rating: isTurbine
        ? (Array.isArray(competency.ratings)
          ? competency.ratings.join(', ')
          : this.joinProcedureValues(this.procedure.ratings))
        : undefined,
      applyToParty: !!competency.applyToParty,
    };
  }

  private addProcedureCompetency(result: { competencyTypeId: number; applyToParty: boolean; manufacturer?: string; models?: string[]; ratings?: string[]; isTurbine?: boolean; }): void {
    const competencies = this.getProcedureCompetencies().slice();
    competencies.push({
      ...result,
      competencyTypeId: result.competencyTypeId,
      applyToParty: result.applyToParty,
      manufacturer: result.manufacturer || '',
      models: this.normalizeProcedureArrayValues(result.models),
      ratings: this.normalizeProcedureArrayValues(result.ratings),
    });

    this.procedureService.updateWorkOrderProcedure(this.buildProcedureUpdatePayload(competencies, {
      manufacturer: result.manufacturer || this.procedure.manufacturer || '',
      models: this.normalizeProcedureArrayValues(result.models).length ? this.normalizeProcedureArrayValues(result.models) : this.normalizeProcedureArrayValues(this.procedure.models),
      ratings: this.normalizeProcedureArrayValues(result.ratings).length ? this.normalizeProcedureArrayValues(result.ratings) : this.normalizeProcedureArrayValues(this.procedure.ratings),
    })).subscribe(
      () => {
        this.notificationService.notify('success', 'Updated!', 'Procedure competency has been updated.');
        this.loadProcedure();
      },
      () => {
        this.notificationService.notify('danger', 'Error', 'Could not update procedure competency');
      },
    );
  }

  private buildProcedureUpdatePayload(
    competencies: any[],
    turbineFields?: { manufacturer?: string; models?: string[]; ratings?: string[]; }
  ) {
    return {
      id: this.procedure.id,
      name: this.procedure.name,
      description: this.procedure.description,
      revision: this.procedure.revision,
      minimumPartySize: this.procedure.minimumPartySize,
      assetCategoryId: this.procedure.assetCategory ? this.procedure.assetCategory.id : null,
      checkOnSiteFormId: this.procedure.checkOnSiteFormId,
      checkOffSiteFormId: this.procedure.checkOffSiteFormId,
      siteId: this.selectedSiteId,
      maintenanceId: this.procedure.maintenanceId,
      preapproved: !!this.procedure.preapproved,
      competencies: competencies.map((item) => this.normalizeProcedureCompetency(item)),
      manufacturer: turbineFields && turbineFields.manufacturer !== undefined ? turbineFields.manufacturer : (this.procedure.manufacturer || ''),
      models: turbineFields && turbineFields.models !== undefined ? turbineFields.models : this.normalizeProcedureArrayValues(this.procedure.models),
      ratings: turbineFields && turbineFields.ratings !== undefined ? turbineFields.ratings : this.normalizeProcedureArrayValues(this.procedure.ratings),
    };
  }

  public saveAssetCategory() {
    if (!this.selectedAssetCategory || !this.selectedAssetCategory.value || !this.procedure) {
      return;
    }

    this.assetCategorySaving = true;

    this.procedureService.updateWorkOrderProcedure({
      ...this.buildProcedureUpdatePayload(this.getProcedureCompetencies()),
      assetCategoryId: this.selectedAssetCategory.value.id,
    }).subscribe(() => {
      this.assetCategorySaving = false;
      this.loadProcedure();
      this.notificationService.notify('success', 'Updated!', 'Asset category has been saved.');
    }, error => {
      this.assetCategorySaving = false;
      throw error;
    });
  }

  public removeAssetCategory() {
    if (!this.procedure || !this.procedure.assetCategory) {
      return;
    }

    this.assetCategorySaving = true;

    this.procedureService.updateWorkOrderProcedure({
      ...this.buildProcedureUpdatePayload(this.getProcedureCompetencies()),
      assetCategoryId: null,
    }).subscribe(() => {
      this.assetCategorySaving = false;
      this.selectedAssetCategory = null;
      this.loadProcedure();
      this.notificationService.notify('success', 'Updated!', 'Asset category has been removed.');
    }, error => {
      this.assetCategorySaving = false;
      throw error;
    });
  }

  public navigateToCreateWorkOrder() {
    this.router.navigate(['../../create-work-order', this.procedure.id], {
      relativeTo: this.activatedRoute,
      queryParams: {
        returnUrl: this.router.url,
      },
    });
  }

  public navigateToEditProcedure() {
    this.router.navigate(['../../edit-procedure', this.procedure.id], {
      relativeTo: this.activatedRoute,
      queryParams: {
        siteOwnerId: this.selectedSite.siteOwner.id,
        allowCompulsoryCheckOnSiteForm: this.selectedSite.siteOwner.siteOwnerUser.allowCompulsoryCheckOnSiteForm,
        allowCompulsoryCheckOffSiteForm: this.selectedSite.siteOwner.siteOwnerUser.allowCompulsoryCheckOffSiteForm,
        returnUrl: this.router.url,
      },
    });
  }

  public deleteProcedure() {
    Swal.fire({
      title: 'Delete Procedure?',
      text: 'You will not be able to revert this!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.deleting = true;
        this.procedureService.deleteWorkOrderProcedure(this.procedure.id).subscribe(() => {
          this.deleting = false;
          this.notificationService.notify('success', 'Deleted!', 'Procedure has been removed.');
          this.navigateBackToList();
        }, error => {
          this.deleting = false;
          throw error;
        });
      }
    });
  }

  public viewDocument(fileName: string): void {
    this.fileService.showFile(this.globals.fileFolder.workOrderProcedureFiles, fileName);
  }

  public saveCheckOnSiteForm() {
    this.updateProcedureSiteForm('checkOn', this.selectedCheckOnSiteForm, 'Check-on site form has been saved.');
  }

  public saveCheckOffSiteForm() {
    this.updateProcedureSiteForm('checkOff', this.selectedCheckOffSiteForm, 'Check-off site form has been saved.');
  }

  public removeCheckOnSiteForm() {
    if (!this.procedure || !this.procedure.checkOnSiteFormId) {
      return;
    }

    this.selectedCheckOnSiteForm = this.findSiteFormOption(null);
    this.updateProcedureSiteForm('checkOn', this.selectedCheckOnSiteForm, 'Check-on site form has been removed.');
  }

  public viewCheckOnSiteForm() {
    if (!this.procedure || !this.procedure.checkOnSiteFormId) {
      return;
    }

    this.navigateToSiteFormDetails(this.procedure.checkOnSiteFormId);
  }

  public removeCheckOffSiteForm() {
    if (!this.procedure || !this.procedure.checkOffSiteFormId) {
      return;
    }

    this.selectedCheckOffSiteForm = this.findSiteFormOption(null);
    this.updateProcedureSiteForm('checkOff', this.selectedCheckOffSiteForm, 'Check-off site form has been removed.');
  }

  public viewCheckOffSiteForm() {
    if (!this.procedure || !this.procedure.checkOffSiteFormId) {
      return;
    }

    this.navigateToSiteFormDetails(this.procedure.checkOffSiteFormId);
  }

  public onDocumentsChange(event) {
    const files = Array.from(event.target.files || []) as File[];
    this.selectedDocuments = files;
  }

  public addDocuments() {
    if (!this.selectedDocuments.length || !this.procedure) {
      return;
    }

    this.documentSaving = true;

    this.procedureService.addDocumentsToProcedure({
      id: this.procedure.id,
      name: this.procedure.name,
      description: this.procedure.description,
      revision: this.procedure.revision,
      minimumPartySize: this.procedure.minimumPartySize,
      assetCategory: this.procedure.assetCategory ? this.procedure.assetCategory.id : null,
      competencies: this.getProcedureCompetencies().map((item) => this.normalizeProcedureCompetency(item)),
      manufacturer: this.procedure.manufacturer || '',
      models: this.normalizeProcedureArrayValues(this.procedure.models),
      ratings: this.normalizeProcedureArrayValues(this.procedure.ratings),
      preapproved: !!this.procedure.preapproved,
      siteId: this.selectedSiteId,
      maintenanceId: this.procedure.maintenanceId,
      checkOnSiteFormId: this.procedure.checkOnSiteFormId,
      checkOffSiteFormId: this.procedure.checkOffSiteFormId,
      documents: this.selectedDocuments,
    }).subscribe(() => {
      this.documentSaving = false;
      this.selectedDocuments = [];
      this.loadProcedure();
      this.notificationService.notify('success', 'Updated!', 'Procedure documents have been uploaded.');
    }, error => {
      this.documentSaving = false;
      throw error;
    });
  }

  public deleteDocument(fileId: number) {
    if (!fileId) {
      return;
    }

    Swal.fire({
      title: 'Delete Procedure Document?',
      text: 'Are you sure? This action cannot be reverted!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.deletingDocumentId = fileId;
        this.fileService.deleteFile(fileId).subscribe(() => {
          this.deletingDocumentId = null;
          this.loadProcedure();
          this.notificationService.notify('success', 'Updated!', 'Procedure document has been removed.');
        }, error => {
          this.deletingDocumentId = null;
          throw error;
        });
      }
    });
  }

  public navigateBackToList() {
    this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
  }

  private updateProcedureSiteForm(type: 'checkOn' | 'checkOff', selectedOption: any, successMessage: string) {
    if (!this.procedure) {
      return;
    }

    if (type === 'checkOn') {
      this.checkOnSiteFormSaving = true;
    } else {
      this.checkOffSiteFormSaving = true;
    }

    const payload = {
      ...this.buildProcedureUpdatePayload(this.getProcedureCompetencies()),
      checkOnSiteFormId: type === 'checkOn' ? (selectedOption ? selectedOption.value : null) : this.procedure.checkOnSiteFormId,
      checkOffSiteFormId: type === 'checkOff' ? (selectedOption ? selectedOption.value : null) : this.procedure.checkOffSiteFormId,
    };

    this.procedureService.updateWorkOrderProcedure(payload).subscribe(() => {
      if (type === 'checkOn') {
        this.checkOnSiteFormSaving = false;
      } else {
        this.checkOffSiteFormSaving = false;
      }
      this.loadProcedure();
      this.notificationService.notify('success', 'Updated!', successMessage);
    }, error => {
      if (type === 'checkOn') {
        this.checkOnSiteFormSaving = false;
      } else {
        this.checkOffSiteFormSaving = false;
      }
      throw error;
    });
  }

  private navigateToSiteFormDetails(siteFormId: number): void {
    const routeRole = this.router.url.split('/')[1];
    this.router.navigate(['/', routeRole, 'site-forms', siteFormId]);
  }

  private getProcedureCompetencies(): any[] {
    return this.procedure && this.procedure.competencies ? this.procedure.competencies : [];
  }

  private normalizeProcedureArrayValues(values: any): string[] {
    return Array.isArray(values)
      ? values
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => String(value))
      : [];
  }

  private getCompetencyTypeId(competency: any): number {
    return Number(competency.competencyTypeId || competency.id);
  }

  private normalizeProcedureCompetency(competency: any): any {
    return {
      competencyTypeId: this.getCompetencyTypeId(competency),
      applyToParty: !!competency.applyToParty,
      manufacturer: competency.manufacturer || '',
      models: this.normalizeProcedureArrayValues(competency.models),
      ratings: this.normalizeProcedureArrayValues(competency.ratings),
    };
  }

  private getTopLevelTurbineFields(competencies: any[]): { manufacturer: string; models: string[]; ratings: string[]; } {
    const turbineCompetency = (competencies || []).find((item) => {
      const type = item && item.type ? item.type : '';
      const isTurbineFromDetails = type === 'TURBINE';
      const hasTurbineFields = !!(item.manufacturer || item.oem || (item.models && item.models.length) || (item.ratings && item.ratings.length));
      return isTurbineFromDetails || hasTurbineFields;
    });

    if (!turbineCompetency) {
      return {
        manufacturer: '',
        models: [],
        ratings: [],
      };
    }

    return {
      manufacturer: turbineCompetency.manufacturer || turbineCompetency.oem || this.procedure.manufacturer || '',
      models: this.normalizeProcedureArrayValues(turbineCompetency.models || turbineCompetency.model || this.procedure.models),
      ratings: this.normalizeProcedureArrayValues(turbineCompetency.ratings || turbineCompetency.rating || this.procedure.ratings),
    };
  }

  private joinProcedureValues(values: any): string | undefined {
    const items = this.normalizeProcedureArrayValues(values);
    return items.length ? items.join(', ') : undefined;
  }
}
